import React from 'react';

import { Modal, Stack, Text } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import { UseMutationResult } from 'react-query';
import { AthletePackage, AthletePackageChange, AthletePackageRetrieve, ErrorMessage } from 'types';

import { ChangeModalProvider, useChangeModal } from './ChangeModalContext';
import ReservationHandlingStep from './components/ReservationHandlingStep';
import ReviewStep from './components/ReviewStep';
import TimingStep from './components/TimingStep';
import Wizard from './components/Wizard';
import AthletePackageCreateForm from '../../../../../AthletePackageCreateForm';

interface ChangeModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeHandler: () => void;
  isDesktop: boolean;
  changeAthletePackageMutation: UseMutationResult<
    AxiosResponse<AthletePackage>,
    AxiosError<ErrorMessage>,
    AthletePackageChange
  >;
}

const ChangeModalShell: React.FC = () => {
  const {
    isDesktop,
    handleClose,
    currentStep,
    hasUpcomingReservations,
    isModalOpen,
    isDrawerOpen,
    lockedStartDate,
    handleCapture,
    handleDrawerCancel,
    athletePackage,
    form,
  } = useChangeModal();

  return (
    <>
      <Modal
        opened={isModalOpen}
        onClose={handleClose}
        size='lg'
        centered
        title={<Text size='lg'>{currentStep}</Text>}
      >
        <Stack p='md' h={isDesktop ? undefined : '90vh'}>
          <Wizard>
            <Wizard.Step name='Timing'>
              <TimingStep />
            </Wizard.Step>
            <Wizard.Step name='Reservation Handling' skip={!hasUpcomingReservations}>
              <ReservationHandlingStep />
            </Wizard.Step>
            <Wizard.Step name='Review'>
              <ReviewStep />
            </Wizard.Step>
          </Wizard>
        </Stack>
      </Modal>

      {lockedStartDate && athletePackage.athlete && (
        <AthletePackageCreateForm
          title={<Text size='lg'>Configure new package</Text>}
          opened={isDrawerOpen}
          closeHandler={handleDrawerCancel}
          athleteId={athletePackage.athlete}
          mode='capture'
          onCapture={handleCapture}
          lockedStartDate={lockedStartDate}
          submitButtonLabel='Continue'
          initialCaptured={form.values.captured}
          inheritedAutoPay={athletePackage.autoPay}
        />
      )}
    </>
  );
};

const ChangeModal: React.FC<ChangeModalProps> = ({
  opened,
  athletePackage,
  closeHandler,
  isDesktop,
  changeAthletePackageMutation,
}) => (
  <ChangeModalProvider
    athletePackage={athletePackage}
    isDesktop={isDesktop}
    closeHandler={closeHandler}
    changeMutation={changeAthletePackageMutation}
    opened={opened}
  >
    <ChangeModalShell />
  </ChangeModalProvider>
);

export default ChangeModal;
